import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { formatLbp, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { EmptyState } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

interface RateRow {
  id: number
  lbpPerUsd: number
  effectiveFromUtc: string
  note: string | null
}

export function ExchangeRateSection() {
  const queryClient = useQueryClient()
  const canChange = useSession((state) => state.can)(Permission.ChangeExchangeRate)

  const [next, setNext] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const current = useQuery({
    queryKey: ['exchange-rate'],
    queryFn: () => apiClient.get<{ lbpPerUsd: number }>('/api/exchange-rate'),
  })

  const history = useQuery({
    queryKey: ['exchange-rate', 'history'],
    queryFn: () => apiClient.get<RateRow[]>('/api/exchange-rate/history'),
  })

  const save = useMutation({
    mutationFn: () =>
      apiClient.post('/api/exchange-rate', {
        lbpPerUsd: Number(next),
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      setNext('')
      setNote('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['exchange-rate'] })
      await queryClient.invalidateQueries({ queryKey: ['checkout'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not set the rate.'),
  })

  const rate = current.data?.lbpPerUsd ?? 0
  const proposed = Number(next || 0)

  return (
    <div>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">
        Prices and profit are kept in dollars; this is what lira are worth when a customer pays
        in them.
      </p>

      <div className="surface-raised mb-5 rounded-[var(--radius-card)] border p-4">
        <p className="text-sm text-[var(--text-secondary)]">{t('Rate in force now')}</p>
        <p className="tabular mt-1 text-3xl font-semibold">{rate.toLocaleString()}</p>
        <p className="text-sm text-[var(--text-secondary)]">{t('Lira to the dollar')}</p>
        {rate > 0 && (
          <p className="tabular mt-3 border-t border-[var(--border-subtle)] pt-3 text-sm text-[var(--text-secondary)]">
            {formatUsd(1)} = {formatLbp(rate)} · {formatUsd(10)} = {formatLbp(rate * 10)}
          </p>
        )}
      </div>

      {canChange ? (
        <div className="surface-raised mb-6 rounded-[var(--radius-card)] border p-4">
          <p className="text-sm font-medium">{t('Set a new rate')}</p>
          <p className="mt-1 mb-4 text-xs text-[var(--text-secondary)]">
            Rates are recorded, never overwritten. Sales already taken keep the rate they were
            charged at, so changing this never rewrites yesterday's takings.
          </p>

          <div className="flex flex-col gap-4">
            <Input
              label={t('Lira to the dollar')}
              type="number"
              inputMode="numeric"
              min="1"
              step="500"
              value={next}
              onChange={(event) => setNext(event.target.value)}
              placeholder={String(rate || 89000)}
              className="tabular text-lg"
            />
            <Input
              label={t('Note')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('Optional — why it moved')}
            />
          </div>

          {proposed > 0 && rate > 0 && (
            <p className="tabular mt-3 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              {rate.toLocaleString()}
              <ArrowRight className="h-4 w-4" />
              {proposed.toLocaleString()}
              <span className={proposed > rate ? 'text-warn-500' : 'text-success-500'}>
                ({proposed > rate ? '+' : ''}
                {(((proposed - rate) / rate) * 100).toFixed(1)}%)
              </span>
            </p>
          )}

          {error && (
            <p role="alert" className="mt-3 flex items-start gap-2 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </p>
          )}

          <Button
            className="mt-4"
            full
            onClick={() => save.mutate()}
            disabled={save.isPending || proposed <= 0}
          >
            {save.isPending ? 'Saving…' : 'Set the rate'}
          </Button>
        </div>
      ) : (
        <p className="surface-sunken mb-6 rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)]">
          {t('Only an administrator can change the rate.')}
        </p>
      )}

      <h3 className="mb-2 text-sm font-semibold">{t('History')}</h3>
      {history.data?.length === 0 ? (
        <EmptyState
          icon={TriangleAlert}
          title={t('No rate history')}
          description={t('Every change is recorded here with who made it.')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {history.data?.map((row, index) => (
            <li
              key={row.id}
              className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="tabular font-medium">
                  {row.lbpPerUsd.toLocaleString()}
                  {index === 0 && (
                    <span className="ml-2 text-xs font-normal text-[var(--accent)]">{t('current')}</span>
                  )}
                </p>
                <p className="truncate text-xs text-[var(--text-secondary)]">
                  {new Date(row.effectiveFromUtc).toLocaleString()}
                  {row.note ? ` · ${row.note}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
